import { MovieClip } from '@play-co/timestep-core/ui';

export function toParticleAmount(num: number, max = 30) {
  return Math.min(
    num / Math.max(Math.pow(10, Math.floor(Math.log10(num)) - 1), 1),
    max,
  );
}

/**
 * Utility for playing a sequence of animations on a MovieClip
 * @param args
 */
export async function playMovieClip(
  movieClip: MovieClip,
  args: {
    animation: string | string[];
    loopLast?: boolean;
    restart?: boolean;
    url?: string;
  },
) {
  if (args.url && movieClip.url !== args.url) {
    await loadMovieClip(movieClip, { url: args.url });
  }

  const animations = Array.isArray(args.animation)
    ? args.animation
    : [args.animation];

  // Don't play again if already playing, unless restart is true
  if (movieClip.isPlaying) {
    const currentAnimation = movieClip['_animationName'];
    if (!args.restart && animations.includes(currentAnimation)) {
      return;
    }
  }

  // Play all animations but the last, in sequence
  const allAnimsButLast = animations.slice(0, -1);
  for (const anim of allAnimsButLast) {
    await movieClip.playAsync(anim);
  }

  // Play last animation, not awaiting if last animation should loop
  const lastAnimation = animations[animations.length - 1];
  if (args.loopLast) {
    movieClip.play(lastAnimation, null, true);
  } else {
    await movieClip.playAsync(lastAnimation);
  }
}

/**
 * Load a new animation into a MovieClip, useful for awaiting the movieclip load
 * before playing any new animation on it, used by `playMovieClip`
 * @param movieClip
 * @param args
 * @returns
 */
async function loadMovieClip(movieClip: MovieClip, args: { url: string }) {
  // Don't load again the same url
  if (movieClip.url === args.url) return;
  movieClip.setData(null);
  const data = await MovieClip.loadAnimation(args.url);
  if (!data) return;
  movieClip.updateOpts({ url: args.url });
  movieClip.setData(data);
}

/**
 * Throttle a function to be called at most once every `delay` milliseconds
 * @param func
 * @param delay
 * @returns
 */
export function throttle(func: () => void, delay: number): () => void {
  let lastCallTime = 0;
  let timeoutId: NodeJS.Timeout | null = null;
  return () => {
    const currentTime = performance.now();
    if (currentTime - lastCallTime < delay) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        lastCallTime = currentTime;
        func();
      }, delay);
    } else {
      lastCallTime = currentTime;
      func();
    }
  };
}

/**
 * Throttle a function to be called at most once every `delay` milliseconds.
 * Ignores additional calls during the delay window.
 * @param func
 * @param delay
 * @returns
 */
export function throttleLeading(func: () => void, delay: number): () => void {
  let lastCallTime = 0;
  return () => {
    const currentTime = performance.now();
    if (currentTime - lastCallTime >= delay) {
      lastCallTime = currentTime;
      func();
    }
    // else: ignore the call
  };
}
